import React, { ReactNode } from 'react';
import { Box, Button, Container, Typography, Alert, Collapse } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ErrorHandler } from '../utils/errorHandler';
import i18n from '../i18n';

interface Props {
  children: ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  errorCount: number;
  showDetails: boolean;
  isRetrying: boolean;
}

/**
 * Enhanced error boundary for React component tree
 * Catches and displays errors without crashing the entire app
 * 
 * Features:
 * - Improved fallback UI with better UX
 * - Retry functionality without full page reload
 * - Error logging to monitoring service in production
 * - Expandable error details in development
 * 
 * Requirements: 6.2, 6.4
 */
export class ErrorBoundary extends React.Component<Props, State> {
  private retryTimeout?: ReturnType<typeof setTimeout>;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorCount: 0,
      showDetails: false,
      isRetrying: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Update error count
    this.setState(prev => ({
      errorCount: prev.errorCount + 1,
      errorInfo,
    }));

    // Handle error using centralized error handler
    const appError = ErrorHandler.handle(error);
    
    // Log error with context
    ErrorHandler.log(appError, {
      componentStack: errorInfo.componentStack,
      errorCount: this.state.errorCount + 1,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, send to monitoring service
    if (import.meta.env.PROD && window.__errorTracker) {
      window.__errorTracker({
        type: 'react-error-boundary',
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        errorCount: this.state.errorCount + 1,
      });
    }
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  /**
   * Retry without full page reload
   * Requirements: 6.2 - Retry functionality without full page reload
   */
  handleRetry = () => {
    this.setState({ isRetrying: true });

    // Small delay to show loading state
    this.retryTimeout = setTimeout(() => {
      this.setState({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        isRetrying: false,
        showDetails: false,
      });
    }, 300);
  };

  handleReload = () => {
    window.location.reload();
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo, errorCount, showDetails, isRetrying } = this.state;
      const tooManyErrors = errorCount > 3;

      return (
        <Container maxWidth="sm">
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="100vh"
            gap={3}
            py={4}
          >
            {/* Error Icon */}
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                backgroundColor: 'error.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.9,
              }}
            >
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />
            </Box>

            {/* Error Title */}
            <Typography variant="h4" component="h1" textAlign="center" fontWeight={600}>
              {i18n.t('errors.somethingWentWrong')}
            </Typography>

            {/* Error Message */}
            <Typography 
              variant="body1" 
              textAlign="center" 
              color="text.secondary"
              sx={{ maxWidth: 500 }}
            >
              {i18n.t('errors.unexpectedError', "We encountered an unexpected error. Don't worry, your data is safe. Please try again or reload the page.")}
            </Typography>

            {/* Warning for multiple errors */}
            {tooManyErrors && (
              <Alert severity="warning" sx={{ width: '100%', maxWidth: 500 }}>
                {i18n.t('errors.multipleErrorsNotice', 'Multiple errors detected. We recommend reloading the page to ensure everything works correctly.')}
              </Alert>
            )}

            {/* Action Buttons */}
            <Box display="flex" gap={2} mt={1} flexWrap="wrap" justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={this.handleRetry}
                disabled={tooManyErrors || isRetrying}
                startIcon={isRetrying ? <RefreshIcon /> : <ReplayIcon />}
                sx={{ minWidth: 140 }}
              >
                {isRetrying ? i18n.t('common.retrying', 'Retrying...') : i18n.t('common.tryAgain', 'Try Again')}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={this.handleReload}
                startIcon={<RefreshIcon />}
                sx={{ minWidth: 140 }}
              >
                {i18n.t('errors.reloadPage')}
              </Button>
            </Box>

            {/* Error Details (Development Only) */}
            {import.meta.env.DEV && error && (
              <Box sx={{ width: '100%', maxWidth: 600, mt: 2 }}>
                <Button
                  onClick={this.toggleDetails}
                  endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  size="small"
                  sx={{ mb: 1 }}
                >
                  {showDetails ? 'Hide' : 'Show'} Error Details
                </Button>
                
                <Collapse in={showDetails}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: 'grey.100',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      maxHeight: 300,
                      overflow: 'auto',
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        margin: 0,
                      }}
                    >
                      <strong>{i18n.t('errors.errorLabel')}</strong> {error.toString()}
                      {'\n\n'}
                      <strong>{i18n.t('errors.stackLabel')}</strong>
                      {'\n'}
                      {error.stack}
                      {errorInfo?.componentStack && (
                        <>
                          {'\n\n'}
                          <strong>{i18n.t('errors.componentStackLabel')}</strong>
                          {'\n'}
                          {errorInfo.componentStack}
                        </>
                      )}
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* Help Text */}
            <Typography 
              variant="caption" 
              color="text.secondary" 
              textAlign="center"
              sx={{ mt: 2 }}
            >
              {i18n.t('errors.contactSupport')}
            </Typography>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

/**
 * Functional wrapper for ErrorBoundary with custom fallback
 */
export const ErrorBoundaryWithFallback: React.FC<{
  children: ReactNode;
  fallback?: React.ReactNode;
}> = ({ children, fallback }) => {
  return <ErrorBoundary fallback={fallback}>{children}</ErrorBoundary>;
};

// Type augmentation for error tracker
declare global {
  interface Window {
    __errorTracker?: (error: any) => void;
  }
}

export default ErrorBoundary;
